import { Injectable, computed, inject, signal, DOCUMENT } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { AppDirectionality } from '../i18n/app-directionality';
import { UiDirection, directionOf } from '../util/direction';

export interface UiLangOption {
  code: string;
  native: string;
}

/** The Extranet's own menus and labels — independent of the content language used on any
 *  property (that's LanguageResponse / listLiveLanguages(), a platform-admin-managed catalog).
 *  Restricted to the languages that actually have an assets/i18n/<code>.json file — offering a
 *  choice ngx-translate can't load would just silently fall back to raw keys.
 *
 *  It also owns the page direction (WI 3974): every language change writes `lang` and `dir` on
 *  `<html>` (Arabic → `dir="rtl"`, the others → `ltr`) and moves the CDK `Directionality`, so the
 *  logical CSS (`margin-inline-start`, `inset-inline-end`…), the `.ext-rtl-flip` icons and the
 *  Material/CDK components all mirror together. Components that need the direction in code read
 *  `dir()` instead of re-deriving it from the language. */
@Injectable({ providedIn: 'root' })
export class UiLangService {
  private translate = inject(TranslateService);
  private document = inject(DOCUMENT);
  private directionality = inject(AppDirectionality);

  private static readonly STORAGE_KEY = 'ext.uiLang';

  readonly LANGS: UiLangOption[] = [
    { code: 'en', native: 'English' },
    { code: 'it', native: 'Italiano' },
    { code: 'ar', native: 'العربية' },
  ];

  readonly current = signal(this.readSaved() ?? 'en');

  /** Direction of the current UI language. */
  readonly dir = computed<UiDirection>(() => directionOf(this.current()));

  /** Applies the saved (or default) language to ngx-translate and the page. Call once, at app startup. */
  init(): void {
    this.translate.use(this.current());
    this.applyToDocument(this.current());
  }

  set(code: string): void {
    if (!this.LANGS.some(l => l.code === code)) return;
    this.translate.use(code);
    this.current.set(code);
    this.applyToDocument(code);
    try { localStorage.setItem(UiLangService.STORAGE_KEY, code); } catch { /* private mode */ }
  }

  /** `<html lang dir>` + the CDK direction: what assistive tech, the browser's bidi algorithm,
   *  the logical CSS and Material all read. */
  private applyToDocument(code: string): void {
    const dir = directionOf(code);
    const html = this.document.documentElement;
    html.lang = code;
    html.dir = dir;
    this.directionality.set(dir);
  }

  private readSaved(): string | null {
    try { return localStorage.getItem(UiLangService.STORAGE_KEY); } catch { return null; }
  }
}
